import dayjs from 'dayjs';
import { ObjectPromotionRewardTypeEnum, ObjectPromotionEntity } from '../../../../generated/graphql';
import { errorToast, successToast } from 'utils/helpers/notify';
import { errorHandler } from 'utils/helpers/errorHandler';
import { usePromotionStore } from 'pages/facilityGroup/useBonusProgramStore';
import { useEditObjectPromotionMutation } from 'pages/facility/api/mutations/generated/EditObjectPromotion';
import { useCreateNewObjectPromotionMutation } from 'pages/facilityGroup/api/mutations/generated/CreateNewPromotion';
import { useFacility } from '../../../../utils/api/useFacility';

export type FacilityBonus = Omit<ObjectPromotionEntity, 'parent'> & {
  dateFrom: dayjs.Dayjs | null;
  timeFrom: dayjs.Dayjs | null;
  dateTo: dayjs.Dayjs | null;
  timeTo: dayjs.Dayjs | null;
  endless: boolean;
  parent?: {
    id: string;
    name: string;
    status: any;
  } | null;
};

type Props = {
  el: FacilityBonus;
  setShowDefault: React.Dispatch<React.SetStateAction<boolean>>;
  setShowEdit: React.Dispatch<React.SetStateAction<boolean>>;
};

const usePromotionForm = ({ el, setShowDefault, setShowEdit }: Props) => {
  const { id } = useFacility();
  const [editPromotionInFacility] = useEditObjectPromotionMutation();
  const [createNewPromotion] = useCreateNewObjectPromotionMutation();
  const changeBonuses = usePromotionStore(state => state.changePromotions);
  const addPromotions = usePromotionStore(state => state.addPromotions);
  const initialValues: FacilityBonus = { ...el };

  const validate = (values: FacilityBonus) => {
    const errors: { [key: string]: string } = {};
    if (!values.name) {
      errors.name = 'Обязательное поле';
    }
    if (!values.requirementShiftCount) {
      errors.requirementShiftCount = 'Обязательное поле';
    }
    if (!values.rewardType) {
      errors.rewardType = 'Обязательное поле';
    }
    if (!values.rewardSum) {
      errors.rewardSum = 'Обязательное поле';
    }
    if (!values.badShiftCount) {
      errors.badShiftCount = 'Обязательное поле';
    }
    if (!values.repeatCondition) {
      errors.repeatCondition = 'Обязательное поле';
    }
    return errors;
  };

  const onSubmit = (values: FacilityBonus) => {
    if (!id) return;

    const variables = {
      name: values.name,
      requirementShiftCount: values.requirementShiftCount!,
      rewardType: values.rewardType as ObjectPromotionRewardTypeEnum,
      rewardSum: values.rewardSum!,
      badShiftCount: values.badShiftCount!,
      repeatCondition: values.repeatCondition,
      startDatetime:
        values.dateFrom && values.timeFrom
          ? values.dateFrom.hour(values.timeFrom.hour()).minute(values.timeFrom.minute()).toISOString()
          : new Date().toISOString(),
    };

    const isDraft = !el.id || el.status === ('Blank' as any);

    if (isDraft) {
      createNewPromotion({
        variables: {
          input: { ...variables, objectId: id },
        },
      })
        .then(response => {
          if (!response.data) return;
          const res = response.data.createNewObjectPromotion;
          successToast('Бонусная программа создана');
          const newPromotion: FacilityBonus = {
            ...res,
            createdAt: new Date().toISOString(), // добавляем обязательное поле
            dateFrom: res.startDatetime ? dayjs(res.startDatetime) : null,
            timeFrom: res.startDatetime ? dayjs(res.startDatetime) : null,
            dateTo: res.endDatetime ? dayjs(res.endDatetime) : null,
            timeTo: res.endDatetime ? dayjs(res.endDatetime) : null,
            endless: !res.endDatetime,
            parent: res.parent
              ? {
                  id: res.parent.id,
                  name: res.parent.name,
                  status: res.parent.status,
                }
              : null,
          };
          addPromotions([newPromotion]);
        })
        .catch(err => {
          errorToast(errorHandler(err));
        })
        .finally(() => {
          setShowDefault(false);
          setShowEdit(false);
        });
    } else {
      editPromotionInFacility({
        variables: {
          input: { ...variables, objectId: id, objectPromotionId: el.id },
        },
      })
        .then(response => {
          if (!response.data) return;
          const res = response.data.editObjectPromotion;
          successToast('Акция изменена');
          changeBonuses(el.id, {
            ...res,
            parent: res.parent
              ? {
                  id: res.parent.id,
                  name: res.parent.name,
                  status: res.parent.status,
                }
              : null,
          });
        })
        .catch(err => {
          errorToast(errorHandler(err));
        })
        .finally(() => {
          setShowDefault(false);
          setShowEdit(false);
        });
    }
  };
  return { initialValues, validate, onSubmit };
};

export default usePromotionForm;
